// ============================================================
// src/utils/specialtyText.js
// ✅ All specialty text mappings in one place
// Import: import { getSpecialtyText } from "../utils/specialtyText";
// Usage:  const t = getSpecialtyText(hospital.type);
// ============================================================

const SPECIALTY_TEXT = {
  dental: {
    // Navbar
    navTagline: "Dental Hospital",
    navServices: "Services",
    navTreatments: "Pricing",
    navDoctors: "Doctors",
    navBook: "Book Appointment",
    // Hero
    heroTitle: "Your Perfect Smile Starts Here.",
    heroTagline: "Your Smile, Our Priority",
    heroDesc:
      "World-class dental care in a calm, compassionate environment. From routine checkups to advanced cosmetic procedures — we deliver results that last a lifetime.",
    heroCTA1: "Book Appointment →",
    heroCTA2: "Explore Services",
    // Sections
    servicesTitle: "Everything Your Smile Needs",
    servicesDesc:
      "Comprehensive dental care under one roof — from preventive treatments to advanced cosmetic procedures.",
    servicesTag: "Our Services",
    whyTag: "Why Choose Us",
    doctorsTitle: "Meet Our Expert Dental Team",
    doctorsDesc:
      "Our experienced dentists bring compassion, cutting-edge skills, and personalised care to every smile they transform.",
    doctorsTag: "Our Specialists",
    // Footer
    footerTagline: "Advanced Dental Care",
    footerDesc:
      "Pune's most trusted dental care centre. Transforming smiles with compassion, expertise, and cutting-edge technology.",
    footerServices: "Dental Services",
    // Appointment
    apptTitle: "Let's Fix Your Smile Today",
    apptDesc:
      "Pick a date and time that suits you. We'll confirm your appointment via WhatsApp within 30 minutes.",
    // Doctor credentials label
    doctorTitle: "Chief Dental Surgeon",
    qualifications: "BDS, MDS",
  },

  ayurved: {
    navTagline: "Ayurvedic Wellness Center",
    navServices: "Therapies",
    navTreatments: "Packages",
    navDoctors: "Our Vaidyas",
    navBook: "Book Consultation",
    heroTitle: "Authentic Ayurvedic Healing Starts Here.",
    heroTagline: "Your Wellness, Our Purpose",
    heroDesc:
      "Time-tested Ayurvedic therapies rooted in ancient wisdom. From Panchakarma detox to personalized wellness plans — we restore balance of body, mind, and spirit.",
    heroCTA1: "Book Consultation →",
    heroCTA2: "Explore Therapies",
    servicesTitle: "Everything Your Wellness Needs",
    servicesDesc:
      "Holistic care for all your health and wellness needs — rooted in the ancient science of Ayurveda.",
    servicesTag: "Our Treatments",
    whyTag: "Why Choose Us",
    doctorsTitle: "Meet Our Expert Vaidyas",
    doctorsDesc:
      "Our experienced Ayurvedic physicians bring deep knowledge, compassionate care, and classical healing techniques to every patient.",
    doctorsTag: "Our Vaidyas",
    footerTagline: "Ancient Healing. Modern Wellness.",
    footerDesc:
      "Pune's most trusted Ayurvedic wellness center. Restoring health with compassion, expertise, and classical techniques.",
    footerServices: "Ayurvedic Therapies",
    apptTitle: "Start Your Healing Journey Today",
    apptDesc:
      "Pick a date and time that suits you. We'll confirm your consultation via WhatsApp within 30 minutes.",
    doctorTitle: "Ayurvedic Physician",
    qualifications: "BAMS, MD (Ayurveda)",
  },

  ortho: {
    navTagline: "Bone & Joint Hospital",
    navServices: "Services",
    navTreatments: "Procedures",
    navDoctors: "Our Surgeons",
    navBook: "Book Consultation",
    heroTitle: "Advanced Bone & Joint Care Starts Here.",
    heroTagline: "Your Mobility, Our Priority",
    heroDesc:
      "World-class orthopedic treatments with the latest surgical technology. From joint replacement to sports injury — we restore movement and eliminate pain.",
    heroCTA1: "Book Consultation →",
    heroCTA2: "Explore Procedures",
    servicesTitle: "Everything Your Joints Need",
    servicesDesc:
      "Comprehensive care for all your bone, joint & muscle needs — from minimally invasive surgery to rehabilitation.",
    servicesTag: "Our Services",
    whyTag: "Why Choose Us",
    doctorsTitle: "Meet Our Expert Orthopedic Surgeons",
    doctorsDesc:
      "Our experienced orthopedic surgeons and rehabilitation specialists are here to get you moving again.",
    doctorsTag: "Our Surgeons",
    footerTagline: "Advanced Orthopedic & Joint Care",
    footerDesc:
      "Pune's most trusted orthopedic hospital. Restoring mobility with surgical excellence and compassionate care.",
    footerServices: "Orthopedic Procedures",
    apptTitle: "Get Pain-Free — Start Today",
    apptDesc:
      "Pick a date and time that suits you. We'll confirm your consultation via WhatsApp within 30 minutes.",
    doctorTitle: "Orthopedic Surgeon",
    qualifications: "MBBS, MS (Ortho), DNB",
  },

  eye: {
    navTagline: "Eye Care Hospital",
    navServices: "Eye Services",
    navTreatments: "Eye Treatments",
    navDoctors: "Our Eye Specialists",
    navBook: "Book Eye Checkup",
    heroTitle: "Advanced Eye Care & Vision Starts Here.",
    heroTagline: "Your Vision, Our Priority",
    heroDesc:
      "World-class eye treatments with the latest optical technology. From LASIK to cataract surgery — we restore and protect your precious gift of vision.",
    heroCTA1: "Book Free Eye Checkup →",
    heroCTA2: "Explore Eye Treatments",
    servicesTitle: "Everything Your Eyes Need",
    servicesDesc:
      "Comprehensive care for all your vision and eye health needs — from diagnosis to advanced surgical procedures.",
    servicesTag: "Our Eye Care Services",
    whyTag: "Why Choose Us",
    doctorsTitle: "Meet Our Expert Eye Specialists",
    doctorsDesc:
      "Our experienced ophthalmologists and vision specialists are here to protect and restore your sight.",
    doctorsTag: "Our Ophthalmologists",
    footerTagline: "Advanced Eye Care & Vision Specialists",
    footerDesc:
      "Pune's most trusted eye care hospital. Protecting vision with surgical excellence and compassionate care.",
    footerServices: "Eye Care Treatments",
    apptTitle: "See Clearly — Start Today",
    apptDesc:
      "Pick a date and time that suits you. We'll confirm your eye checkup via WhatsApp within 30 minutes.",
    doctorTitle: "Ophthalmologist",
    qualifications: "MBBS, MS (Ophthalmology), DNB",
  },

  cardiology: {
    navTagline: "Cardiology Hospital",
    navServices: "Cardiac Services",
    navTreatments: "Cardiac Treatments",
    navDoctors: "Our Cardiologists",
    navBook: "Book Cardiac Consultation",
    heroTitle: "Advanced Heart & Cardiac Care Starts Here.",
    heroTagline: "Your Heart, Our Priority",
    heroDesc:
      "World-class cardiac treatments with the latest cardiovascular technology. From angioplasty to open heart surgery — we protect every heartbeat.",
    heroCTA1: "Book Free Cardiac Consultation →",
    heroCTA2: "Explore Cardiac Treatments",
    servicesTitle: "Everything Your Heart Needs",
    servicesDesc:
      "Comprehensive care for all your heart & cardiovascular needs — from preventive cardiology to complex cardiac surgery.",
    servicesTag: "Our Cardiology Services",
    whyTag: "Why Choose Us",
    doctorsTitle: "Meet Our Expert Cardiologists",
    doctorsDesc:
      "Our experienced cardiologists and cardiac surgeons are here to keep your heart beating strong.",
    doctorsTag: "Our Cardiologists",
    footerTagline: "Advanced Heart & Cardiovascular Care",
    footerDesc:
      "Pune's most trusted cardiology hospital. Protecting hearts with surgical excellence and compassionate care.",
    footerServices: "Cardiology Treatments",
    apptTitle: "Protect Your Heart — Start Today",
    apptDesc:
      "Pick a date and time that suits you. We'll confirm your cardiac consultation via WhatsApp within 30 minutes.",
    doctorTitle: "Interventional Cardiologist",
    qualifications: "MBBS, MD (Cardiology), DM",
  },

  pediatric: {
    navTagline: "Children's Hospital",
    navServices: "Pediatric Services",
    navTreatments: "Child Treatments",
    navDoctors: "Our Pediatricians",
    navBook: "Book Consultation",
    heroTitle: "Advanced Child Healthcare Starts Here.",
    heroTagline: "Your Child's Health, Our Priority",
    heroDesc:
      "World-class pediatric care with the most child-friendly approach. From newborn care to adolescent health — we deliver gentle, expert care for every child.",
    heroCTA1: "Book Free Pediatric Consultation →",
    heroCTA2: "Explore Child Care Services",
    servicesTitle: "Everything Your Child Needs",
    servicesDesc:
      "Comprehensive healthcare for newborns, infants, children & adolescents — in a safe, child-friendly environment.",
    servicesTag: "Our Pediatric Services",
    whyTag: "Why Choose Us",
    doctorsTitle: "Meet Our Expert Pediatric Specialists",
    doctorsDesc:
      "Our experienced pediatricians and child health specialists are here to keep your child healthy and happy.",
    doctorsTag: "Our Pediatricians",
    footerTagline: "Advanced Pediatric & Child Healthcare",
    footerDesc:
      "Pune's most trusted children's hospital. Caring for little ones with expertise, gentleness, and love.",
    footerServices: "Pediatric Treatments",
    apptTitle: "Your Child's Health Starts Here",
    apptDesc:
      "Pick a date and time that suits you. We'll confirm your child's consultation via WhatsApp within 30 minutes.",
    doctorTitle: "Pediatrician",
    qualifications: "MBBS, MD (Pediatrics), DCH",
  },

  general: {
    navTagline: "Multi-Specialty Hospital",
    navServices: "Medical Services",
    navTreatments: "Treatments",
    navDoctors: "Our Doctors",
    navBook: "Book Appointment",
    heroTitle: "Advanced Multi-Specialty Healthcare Starts Here.",
    heroTagline: "Your Health, Our Priority",
    heroDesc:
      "World-class medical treatments with the latest healthcare technology. From general medicine to complex surgery — we deliver compassionate, quality healthcare to all.",
    heroCTA1: "Book Appointment →",
    heroCTA2: "Explore Medical Services",
    servicesTitle: "Everything Your Health Needs",
    servicesDesc:
      "Comprehensive healthcare for all ages and all medical conditions — under one roof.",
    servicesTag: "Our Medical Services",
    whyTag: "Why Choose Us",
    doctorsTitle: "Meet Our Expert Medical Team",
    doctorsDesc:
      "Our experienced doctors, surgeons and healthcare professionals are here for your complete health and recovery.",
    doctorsTag: "Our Specialists",
    footerTagline: "Advanced Multi-Specialty Healthcare",
    footerDesc:
      "Pune's most trusted multi-specialty hospital. Delivering quality healthcare with compassion and excellence.",
    footerServices: "Medical Treatments",
    apptTitle: "Your Health Journey Starts Here",
    apptDesc:
      "Pick a date and time that suits you. We'll confirm your appointment via WhatsApp within 30 minutes.",
    doctorTitle: "General Physician",
    qualifications: "MBBS, MD, MS, DNB",
  },
};

// ✅ Main function — returns text for given hospital type
export function getSpecialtyText(type) {
  const key = (type || "dental").toLowerCase();
  return SPECIALTY_TEXT[key] || SPECIALTY_TEXT.dental;
}

// ✅ Default dental text
export const DEFAULT_TEXT = SPECIALTY_TEXT.dental;
